import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, InfoOutlined } from '@mui/icons-material';
import ProductTimeline from './ProductTimeline';
import TouchTooltip from '../../common/TouchTooltip';
import { dashboardAPI } from '../../../services/dashboard-api';
import { formatDate, formatRange } from '../../../utils/buyerInsightsDates';
import { fmtInt, sizeStatusLabel, soldOutAtText, weeksBeforeSaleText } from '../../../utils/buyerInsightsFormat';
import { saleStartTooltip } from './constants';

const cache = new Map(); // `${id}|${view}|${n}|${filters}` → rows

// "6.4 weeks before your sale (YAYA sale from 28 Jun)" with the rule on an ⓘ (sold-out-at spec §2.4):
// the rule and the date are never hidden behind the number.
const WeeksBeforeSale = ({ row, brand }) => {
  const text = weeksBeforeSaleText(row, brand);
  if (!text) return null;
  const date = row.your_sale_start ? formatDate(row.your_sale_start) : '';
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>{text}</span>
      <TouchTooltip title={saleStartTooltip(brand, date)}>
        <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
      </TouchTooltip>
    </Typography>
  );
};

// Expanded row (spec §2.7): one row per size, columns per side; cards below the breakpoint.
// Fetched on first expand, cached per product + filters (the table follows every filter of the
// list, so its sizes add up to the row). The Timeline toggle opens the product timeline (spec §3)
// below the table; not offered for inserted sizes (no stock history).
const SizeTable = ({ productId, view, window, n, myRetailerId, isWide, side, filters, brand }) => {
  const filterPart = filters ? JSON.stringify(filters) : `${window.start}|${window.end}|${myRetailerId || ''}`;
  const key = `${productId}|${view}|${n}|${filterPart}`;
  const [rows, setRows] = useState(() => cache.get(key) || null);
  const [error, setError] = useState('');
  const [timelineOpen, setTimelineOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const cached = cache.get(key);
    if (cached) {
      setRows(cached);
      return undefined;
    }
    seq.current += 1;
    const mine = seq.current;
    setRows(null);
    setError('');
    const params = { ...(filters || { start: window.start, end: window.end }), view };
    if (view === 'still_wanted') params.n = n;
    if (!filters && myRetailerId) params.my_retailer_id = myRetailerId;
    dashboardAPI
      .getBuyerInsightsProductSizes(productId, params)
      .then((res) => {
        if (mine !== seq.current) return;
        if (res && res.success) {
          cache.set(key, res.data.rows);
          setRows(res.data.rows);
        } else {
          setError((res && res.error) || 'Could not load sizes');
        }
      })
      .catch((e) => mine === seq.current && setError(e.message || 'Could not load sizes'));
    return undefined;
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    { key: 'label', label: 'Size' },
    { key: 'status', label: 'Status' },
    { key: 'status_date', label: 'Status date' },
  ];
  const withSoldOutAt = view !== 'never_bought' && view !== 'only_you';
  if (withSoldOutAt) columns.push({ key: 'sold_out_at', label: 'Sold out at' });
  const textColumns = columns.length;
  if (side !== 'sent') columns.push({ key: 'r_clicks', label: 'Clicks received' }, { key: 'r_orders', label: 'Orders received' });
  if (side !== 'received') {
    columns.push({ key: 's_matches', label: 'Matches shown' }, { key: 's_clicks', label: 'Clicks sent' }, { key: 's_orders', label: 'Orders sent' });
  }
  columns.push({ key: 'last_click', label: 'Last click' }, { key: 'last_order', label: 'Last order' });

  const cell = (r, k) => {
    switch (k) {
      case 'label':
        return r.label;
      case 'status':
        return sizeStatusLabel(r.status) + (r.listed_on_end === false ? ' · no longer listed' : '');
      case 'status_date':
        if (r.status_windows && r.status_windows.length) {
          // Sizes only you have: the window(s) only you had it in stock
          return r.status_windows.map((w) => (w.to ? formatRange(w.from, w.to) : `since ${formatDate(w.from)}`)).join(', ');
        }
        return r.status_date ? formatDate(r.status_date) : '—';
      case 'sold_out_at':
        return (
          <>
            <span>{soldOutAtText(r.sold_out_at)}</span>
            <WeeksBeforeSale row={r} brand={brand} />
          </>
        );
      case 'r_clicks':
        return fmtInt(r.received?.clicks);
      case 'r_orders':
        return fmtInt(r.received?.orders);
      case 's_matches':
        return fmtInt(r.sent?.matches_shown);
      case 's_clicks':
        return fmtInt(r.sent?.clicks);
      case 's_orders':
        return fmtInt(r.sent?.orders);
      case 'last_click':
        return r.last_click ? formatDate(r.last_click) : '—';
      case 'last_order':
        return r.last_order ? formatDate(r.last_order) : '—';
      default:
        return '';
    }
  };

  const timeline = view !== 'never_bought' && (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        onClick={() => setTimelineOpen((v) => !v)}
        endIcon={timelineOpen ? <ExpandLess /> : <ExpandMore />}
        sx={{ minHeight: isWide ? 32 : 44 }}
        aria-expanded={timelineOpen}
      >
        Timeline
      </Button>
      {timelineOpen && <ProductTimeline productId={productId} view={view} window={window} myRetailerId={myRetailerId} isWide={isWide} />}
    </Box>
  );

  if (error) return <Alert severity="error" variant="outlined" sx={{ my: 1 }}>{error}</Alert>;
  if (!rows) return <Skeleton variant="rectangular" height={isWide ? 96 : 160} sx={{ borderRadius: 1, my: 1 }} />;
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 1 }}>
        No sizes in this period.
      </Typography>
    );
  }

  if (!isWide) {
    return (
      <Box sx={{ my: 1 }}>
        {rows.map((r) => (
          <Paper key={r.size_id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Size {r.label}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 0.25, columnGap: 1 }}>
              {columns.slice(1).map((c) => (
                <React.Fragment key={c.key}>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                  <Typography variant="caption" component="div" sx={{ textAlign: 'right' }}>{cell(r, c.key)}</Typography>
                </React.Fragment>
              ))}
            </Box>
          </Paper>
        ))}
        {timeline}
      </Box>
    );
  }

  return (
    <Box sx={{ my: 1 }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Sizes">
          <TableHead>
            <TableRow>
              {columns.map((c, i) => (
                <TableCell key={c.key} align={i < textColumns ? 'left' : 'right'} sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.size_id} hover>
                {columns.map((c, i) => (
                  <TableCell key={c.key} align={i < textColumns ? 'left' : 'right'} sx={{ whiteSpace: 'nowrap' }}>
                    {cell(r, c.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {timeline}
    </Box>
  );
};

export default SizeTable;
